import { useState } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import ListingTypeConverter from './ListingTypeConverter';
import PromotionPurchase from './PromotionPurchase';

export default function EditListingModal({ listing, isOpen, onClose, onSave, onDelete }) {
  const [price, setPrice] = useState(listing?.price || '');
  const [status, setStatus] = useState(listing?.status || 'active');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTypeConverter, setShowTypeConverter] = useState(false);
  const [showPromotionPurchase, setShowPromotionPurchase] = useState(false);

  if (!isOpen || !listing) return null;

  const handleSave = async () => {
    if (!price || parseFloat(price) <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }

    setSaving(true);
    try {
      await API.put(`/listings/${listing.id}`, {
        price: parseFloat(price),
        status
      });
      toast.success('Listing updated!');
      onSave?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update listing');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await API.delete(`/listings/${listing.id}`);
      toast.success('Listing removed');
      onDelete?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete listing');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 998,
          backdropFilter: 'blur(2px)'
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          borderRadius: 16,
          padding: 32,
          zIndex: 999,
          maxWidth: 480,
          width: 'calc(100% - 32px)',
          boxShadow: '0 20px 56px rgba(26,18,10,0.13)',
          animation: 'scaleIn 0.3s ease'
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
            Edit Listing
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-soft)' }}>
            {listing.title}
          </p>
        </div>

        {/* Fields */}
        <div style={{ marginBottom: 24 }}>
          {/* Price */}
          <div className="form-group">
            <label className="form-label">Price</label>
            <div className="price-input-wrap">
              <span className="price-prefix">$</span>
              <input
                className="price-field"
                type="number"
                value={price}
                onChange={e => setPrice(e.target.value)}
                step="0.01"
                min="0"
                placeholder="0.00"
              />
            </div>
            {listing.listing_type === 'rent' && (
              <div className="form-hint">/day</div>
            )}
          </div>

          {/* Status */}
          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={status}
              onChange={e => setStatus(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="sold">{listing.listing_type === 'rent' ? 'Rented' : 'Sold'}</option>
              <option value="archived">Remove Listing</option>
            </select>
            <div className="form-hint">
              {status === 'archived' ? 'Listing will be hidden from search' : ''}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-cherry"
            style={{ flex: 1 }}
          >
            {saving ? '💾 Saving...' : '💾 Save Changes'}
          </button>
          <button
            onClick={onClose}
            disabled={saving}
            className="btn btn-ghost"
            style={{ flex: 1 }}
          >
            Cancel
          </button>
        </div>

        {/* Edit Full, Change Type & Delete Buttons */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <button
            onClick={() => window.location.href = `/listing/${listing.id}/edit`}
            className="btn"
            style={{
              background: 'var(--cream)',
              color: 'var(--text-mid)',
              border: 'none',
              fontSize: 13
            }}
          >
            ✏️ Edit Details
          </button>

          <button
            onClick={() => setShowTypeConverter(true)}
            className="btn"
            style={{
              background: '#F3E5F5',
              color: '#7B1FA2',
              border: '1px solid rgba(123,31,162,0.2)',
              fontSize: 13
            }}
          >
            🔄 Change Type
          </button>

          <button
            onClick={() => setShowPromotionPurchase(true)}
            className="btn"
            style={{
              background: '#FFF3E0',
              color: '#E65100',
              border: '1px solid rgba(230,81,0,0.2)',
              fontSize: 13
            }}
          >
            ⭐ Promote
          </button>
        </div>

        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="btn btn-full"
          style={{
            background: '#FDECEA',
            color: 'var(--cherry)',
            border: '1px solid rgba(192,57,43,0.2)',
            fontSize: 13
          }}
        >
          🗑️ Remove Listing
        </button>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 1000,
              backdropFilter: 'blur(2px)'
            }}
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: 16,
              padding: 32,
              zIndex: 1001,
              maxWidth: 400,
              width: 'calc(100% - 32px)',
              boxShadow: '0 20px 56px rgba(26,18,10,0.13)',
              animation: 'scaleIn 0.3s ease'
            }}
          >
            <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
              Remove listing?
            </h3>
            <p style={{ fontSize: 14, color: 'var(--text-mid)', marginBottom: 24, lineHeight: 1.6 }}>
              This will remove "{listing.title}" from the marketplace. You can always re-list it later.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-ghost"
              >
                Keep It
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="btn"
                style={{
                  background: '#F44336',
                  color: 'white',
                  border: 'none'
                }}
              >
                {deleting ? '⏳ Removing...' : '🗑️ Remove'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Type Converter Modal */}
      <ListingTypeConverter
        listing={listing}
        isOpen={showTypeConverter}
        onClose={() => setShowTypeConverter(false)}
        onConvert={() => {
          // Refresh parent listing data
          window.location.reload();
        }}
      />

      {/* Promotion Purchase Modal */}
      <PromotionPurchase
        listing={listing}
        isOpen={showPromotionPurchase}
        onClose={() => setShowPromotionPurchase(false)}
        onPurchase={() => {
          toast.success('Promotion activated!');
          setShowPromotionPurchase(false);
          onClose?.();
        }}
      />
    </>
  );
}
